import { supabase } from '@/integrations/supabase/client';
import { 
  AuthError, 
  AuthResponse, 
  AuthSession, 
  Provider, 
  UserResponse 
} from '@supabase/supabase-js';
import { unifiedSessionService } from './unifiedSessionService';

/**
 * Core Authentication Service
 * Handles all authentication operations with Supabase
 */
export class AuthService {
  private static failedAttemptsMap = new Map<string, { count: number, lockedUntil: Date | null }>();
  private static MAX_FAILED_ATTEMPTS = 5;
  private static LOCKOUT_DURATION_MS = 30 * 60 * 1000; // 30 minutes in ms
  
  /**
   * Sign in with email and password
   */
  static async signInWithPassword(email: string, password: string): Promise<AuthResponse> {
    // Check if account is locked
    const isLocked = this.isAccountLocked(email);
    if (isLocked.locked) {
      const errorMessage = `This account is temporarily locked due to too many failed attempts. Please try again in ${Math.ceil((isLocked.remainingTime || 0) / (60 * 1000))} minutes.`;
      throw new Error(errorMessage);
    }
    
    try {
      // Attempt to sign in
      const response = await supabase.auth.signInWithPassword({ email, password });
      
      if (response.error) {
        this.recordFailedAttempt(email);
        throw response.error;
      }
      
      // Reset failed attempts on successful login
      this.resetFailedAttempts(email);
      return response;
    } catch (error) {
      // Ensure any error is properly converted to an AuthError or Error
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown error occurred during sign in');
    }
  }
  
  /**
   * Sign up with email, password and optional user metadata
   */
  static async signUp(
    email: string, 
    password: string, 
    options?: { 
      username?: string,
      redirectTo?: string,
      data?: Record<string, any>
    }
  ): Promise<AuthResponse> {
    try {
      const signUpOptions = {
        emailRedirectTo: options?.redirectTo || window.location.origin + '/account',
        data: {
          username: options?.username || email.split('@')[0],
          display_name: options?.username || email.split('@')[0],
          ...options?.data
        }
      };
      
      const response = await supabase.auth.signUp({
        email,
        password,
        options: signUpOptions
      });
      
      if (response.error) {
        throw response.error;
      }
      
      return response;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown error occurred during sign up');
    }
  }
  
  /**
   * Sign out user from current session or all sessions
   */
  static async signOut(scope: 'local' | 'global' = 'local'): Promise<{ error: Error | null }> {
    try {
      // Clear session data through unified service
      // Note: unifiedSessionService will handle cleanup automatically on signout
      
      const { error } = await supabase.auth.signOut({ scope });
      
      if (error) {
        throw error;
      }
      
      // For global sign out, optionally perform additional cleanup
      if (scope === 'global') {
        // Clear all session storage
        sessionStorage.clear();
      }
      
      return { error: null };
    } catch (error) {
      console.error('Error during sign out:', error);
      return { 
        error: error instanceof Error 
          ? error 
          : new Error('An unknown error occurred during sign out')
      };
    }
  }
  
  /**
   * Reset password for user
   */
  static async resetPassword(email: string, redirectUrl?: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl || window.location.origin + '/reset-password',
      });
      
      if (error) {
        throw error;
      }
      
      return { error: null };
    } catch (error) {
      return { 
        error: error instanceof Error 
          ? error 
          : new Error('An unknown error occurred during password reset')
      };
    }
  }
  
  /**
   * Update user password
   */
  static async updatePassword(newPassword: string): Promise<UserResponse> {
    try {
      const response = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (response.error) {
        throw response.error;
      }
      
      return response;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown error occurred when updating password');
    }
  }
  
  /**
   * Get current session
   */
  static async getSession(): Promise<AuthSession | null> {
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting session:', error);
        return null;
      }
      
      return data.session;
    } catch (error) {
      console.error('Exception getting session:', error);
      return null;
    }
  }
  
  /**
   * Update user email or other details
   */
  static async updateUser(attributes: { email?: string, [key: string]: any }): Promise<UserResponse> {
    return await supabase.auth.updateUser(attributes);
  }
  
  /**
   * Sign in with a third-party provider
   */
  static async signInWithProvider(provider: Provider): Promise<any> {
    return await supabase.auth.signInWithOAuth({ provider });
  }
  
  /**
   * Check if an account is locked due to too many failed attempts
   */
  static isAccountLocked(email: string): { locked: boolean, remainingTime: number | null } {
    const lowerEmail = email.toLowerCase();
    const record = this.failedAttemptsMap.get(lowerEmail);
    
    if (!record) {
      return { locked: false, remainingTime: null };
    }
    
    if (record.lockedUntil && record.lockedUntil > new Date()) {
      const remainingTime = record.lockedUntil.getTime() - new Date().getTime();
      return { locked: true, remainingTime };
    }
    
    // If lock has expired, reset the record
    if (record.lockedUntil && record.lockedUntil <= new Date()) {
      this.resetFailedAttempts(lowerEmail);
    }
    
    return { locked: false, remainingTime: null };
  }
  
  /**
   * Record a failed login attempt
   */
  static recordFailedAttempt(email: string): void {
    const lowerEmail = email.toLowerCase();
    const record = this.failedAttemptsMap.get(lowerEmail) || { count: 0, lockedUntil: null };
    
    record.count += 1;
    
    // Lock account if max attempts reached
    if (record.count >= this.MAX_FAILED_ATTEMPTS) {
      record.lockedUntil = new Date(Date.now() + this.LOCKOUT_DURATION_MS);
    }
    
    this.failedAttemptsMap.set(lowerEmail, record);
    
    // Store in session storage for persistence
    this.persistFailedAttempts();
  }
  
  /**
   * Reset failed attempts counter for an account
   */
  static resetFailedAttempts(email: string): void {
    const lowerEmail = email.toLowerCase();
    this.failedAttemptsMap.delete(lowerEmail);
    this.persistFailedAttempts();
  }
  
  /**
   * Persist failed attempts to session storage
   */
  private static persistFailedAttempts(): void {
    const data: Record<string, { count: number, lockedUntil: string | null }> = {};
    
    this.failedAttemptsMap.forEach((value, key) => {
      data[key] = {
        count: value.count,
        lockedUntil: value.lockedUntil ? value.lockedUntil.toISOString() : null
      };
    });
    
    sessionStorage.setItem('auth_failed_attempts', JSON.stringify(data));
  }
  
  /**
   * Load failed attempts from session storage
   */
  static loadFailedAttempts(): void {
    try {
      const data = sessionStorage.getItem('auth_failed_attempts');
      
      if (!data) return;
      
      const parsed = JSON.parse(data) as Record<string, { count: number, lockedUntil: string | null }>;
      
      Object.entries(parsed).forEach(([email, value]) => {
        this.failedAttemptsMap.set(email, {
          count: value.count,
          lockedUntil: value.lockedUntil ? new Date(value.lockedUntil) : null
        });
      });
    } catch (error) {
      console.error('Error loading failed attempts:', error);
    }
  }
}

// Load failed attempts on module initialization
AuthService.loadFailedAttempts();
